import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { listOEMPartners, setCustomerOEM, type OEMPartner } from '../../services/oemAdmin';
import { CustomerOEMCard, CustomerOEMFilter } from './CustomerOEM';
import { customerOEMLabel, filterCustomersByOEM } from '../oem/oemModel';
import {
  ApiError,
  Department,
  DepartmentDetail,
  InventoryRadio,
  RoleAssignment,
  ScopedResource,
  TENANT_ROLES,
  Tenant,
  TenantUser,
  createDepartment,
  createTenant,
  deleteDepartment,
  getDepartment,
  getDepartments,
  getInventory,
  getRoleAssignments,
  getTenantGroups,
  getTenantUsers,
  getTenants,
  grantRole,
  renameTenant,
  revokeRole,
  updateDepartment,
  updateDepartmentScopes,
} from '../../services/tenancy';
import { useIdentity, type Me } from '../../services/identity';
import CustomerDeleteCard from './CustomerDelete';
import CustomerNetworks from './CustomerNetworks';

// Customers (tenants), their networks, departments (optional authorization scopes) and role +
// scope assignments (documentation/architecture.md "Tenancy"). Radios are assigned to networks in
// the Rental Inventory tab; departments only add optional visibility scopes on top.

const input = 'bg-gray-900 border border-gray-600 rounded px-3 py-1.5 text-sm';
const btn = 'px-3 py-1.5 rounded text-sm font-medium disabled:opacity-40';

function message(err: unknown): string {
  if (err instanceof ApiError) return err.code ? `${err.message} (${err.code})` : err.message;
  return err instanceof Error ? err.message : String(err);
}

// May the caller manage departments/roles of a tenant (tenant-wide departments.manage)?
function managesTenant(me: Me | null, tenantId: string): boolean {
  if (!me || me.platform_admin) return true;
  return me.networks.some((n) => n.tenant_id === tenantId && n.tenant_wide && n.permissions.includes('departments.manage'));
}

export default function TenantsManager() {
  const { me } = useIdentity();
  const platform = !me || me.platform_admin;
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [searchParams] = useSearchParams();
  const [tenantId, setTenantId] = useState(searchParams.get('customer') || '');
  const [oemPartners, setOEMPartners] = useState<OEMPartner[]>([]);
  const [oemFilter, setOEMFilter] = useState('all');
  const [error, setError] = useState<string | null>(null);
  const [newTenant, setNewTenant] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const loadTenants = async () => {
    try {
      const list = await getTenants();
      setTenants(list);
      if (!tenantId && list.length) setTenantId(list[0].id);
    } catch (err) {
      setError(message(err));
    }
  };

  useEffect(() => {
    loadTenants();
    // OEM Partners are platform administration (the server refuses them to everyone else).
    if (platform) listOEMPartners().then(setOEMPartners).catch(() => setOEMPartners([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await loadTenants();
    } catch (err) {
      setError(message(err));
    }
  };

  const tenant = tenants.find((t) => t.id === tenantId);
  const shown = platform ? filterCustomersByOEM(tenants, oemFilter) : tenants;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold">Customers</h2>
        <p className="text-sm text-gray-400">
          A customer (tenant) is the security boundary. It owns one or more radio networks and, optionally, departments that scope
          which radios and groups a department&apos;s users may act on. Role (what a user may do) and scope (where) are assigned separately.
        </p>
      </div>
      {error && (
        <div className="p-3 rounded bg-red-900/50 border border-red-700 text-red-200 text-sm flex justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} aria-label="Dismiss">×</button>
        </div>
      )}
      {notice && (
        <div role="status" className="p-3 rounded bg-green-900/40 border border-green-700 text-green-200 text-sm flex justify-between">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} aria-label="Dismiss">×</button>
        </div>
      )}
      <div className="flex flex-col lg:flex-row gap-4">
        <aside className="lg:w-64 shrink-0 space-y-2">
          {platform && <div className="flex gap-2">
            <input value={newTenant} onChange={(e) => setNewTenant(e.target.value)} placeholder="New customer" className={`${input} flex-1 min-w-0`} />
            <button className={`${btn} bg-blue-600 hover:bg-blue-500`} disabled={!newTenant.trim()}
              onClick={() => run(async () => { const { id } = await createTenant(newTenant); setNewTenant(''); setTenantId(id); })}>Add</button>
          </div>}
          {platform && <CustomerOEMFilter value={oemFilter} onChange={setOEMFilter} partners={oemPartners} />}
          <ul className="bg-gray-800 border border-gray-700 rounded divide-y divide-gray-700">
            {shown.length === 0 && <li className="px-3 py-2 text-sm text-gray-400">No customers match this filter.</li>}
            {shown.map((t) => (
              <li key={t.id}>
                <button onClick={() => setTenantId(t.id)}
                  className={`w-full text-left px-3 py-2 text-sm ${t.id === tenantId ? 'bg-blue-700' : 'hover:bg-gray-700'}`}>
                  <div className="font-medium">{t.name}</div>
                  <div className="text-xs text-gray-400">{t.networks.length} network(s) · {t.department_count} dept · {t.user_count} users</div>
                  {platform && <div className="text-xs text-gray-300">OEM Partner: {customerOEMLabel(t)}</div>}
                </button>
              </li>
            ))}
          </ul>
        </aside>
        {tenant && <TenantPanel key={tenant.id} tenant={tenant} tenants={tenants} run={run} onError={setError} onNotice={setNotice} platform={platform} manage={managesTenant(me, tenant.id)} me={me}
          oemCard={platform ? <>
            <CustomerOEMCard tenant={tenant} partners={oemPartners} onChange={(oemId) => run(() => setCustomerOEM(tenant.id, oemId))} />
            <CustomerDeleteCard tenant={tenant} onError={setError}
              onDeleted={(n) => { setTenantId(''); setNotice(n); loadTenants(); }} />
          </> : null} />}
      </div>
    </div>
  );
}

function TenantPanel({ tenant, tenants, run, onError, onNotice, platform, manage, me, oemCard }: {
  oemCard: ReactNode;
  onNotice: (msg: string) => void;
  platform: boolean;
  manage: boolean;
  me: Me | null;
  tenant: Tenant;
  tenants: Tenant[];
  run: (fn: () => Promise<unknown>) => Promise<void>;
  onError: (msg: string | null) => void;
}) {
  const [name, setName] = useState(tenant.name);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [deptName, setDeptName] = useState('');
  const [deptDescription, setDeptDescription] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [roles, setRoles] = useState<RoleAssignment[]>([]);
  const [grant, setGrant] = useState({ user: '', role: 'dispatcher', department: '' });

  const loadTenantData = async () => {
    try {
      // Users/roles are tenant-administrator data; a department user only reads its departments.
      const [d, u, r] = await Promise.all([getDepartments(tenant.id), manage ? getTenantUsers(tenant.id) : [], manage ? getRoleAssignments(tenant.id) : []]);
      setDepartments(d);
      setUsers(u);
      setRoles(r);
    } catch (err) {
      onError(message(err));
    }
  };

  useEffect(() => {
    loadTenantData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.id]);

  const act = (fn: () => Promise<unknown>) => run(async () => { await fn(); await loadTenantData(); });

  return (
    <section className="flex-1 min-w-0 space-y-4">
      <div className="bg-gray-800 border border-gray-700 rounded p-4 space-y-2">
        {!platform && <h3 className="text-base font-semibold">{tenant.name}</h3>}
        {platform && <div className="flex flex-wrap gap-2 items-center">
          <input value={name} onChange={(e) => setName(e.target.value)} className={`${input} text-base font-semibold flex-1 min-w-[12rem]`} aria-label="Customer name" />
          <button className={`${btn} bg-gray-700 hover:bg-gray-600`} disabled={!name.trim() || name === tenant.name}
            onClick={() => run(() => renameTenant(tenant.id, name))}>Rename</button>
        </div>}
        <p className="text-xs text-gray-500 font-mono">{tenant.id}</p>
      </div>

      {oemCard}

      <CustomerNetworks tenant={tenant} tenants={tenants} platform={platform} run={run} onNotice={onNotice} onError={onError} />

      <div className="bg-gray-800 border border-gray-700 rounded p-4 space-y-3">
        <h3 className="font-semibold">Departments <span className="text-xs font-normal text-gray-400">optional authorization scopes</span></h3>
        <div className="flex flex-wrap gap-2">
          {departments.map((d) => (
            <button key={d.id} onClick={() => setDepartmentId(d.id === departmentId ? '' : d.id)}
              className={`px-3 py-1.5 rounded border text-sm ${d.id === departmentId ? 'bg-blue-700 border-blue-500' : 'border-gray-600 hover:bg-gray-700'}`}>
              {d.name} <span className="text-xs text-gray-300">({d.radio_count} radios · {d.group_count} groups · {d.member_count} users)</span>
            </button>
          ))}
          {departments.length === 0 && <span className="text-sm text-gray-400">No departments – every radio and group is tenant-wide.</span>}
        </div>
        {manage && <div className="flex flex-wrap gap-2">
          <input value={deptName} onChange={(e) => setDeptName(e.target.value)} placeholder="Department name" className={input} />
          <input value={deptDescription} onChange={(e) => setDeptDescription(e.target.value)} placeholder="Description (optional)" className={`${input} flex-1`} />
          <button className={`${btn} bg-blue-600 hover:bg-blue-500`} disabled={!deptName.trim()}
            onClick={() => act(async () => { await createDepartment(tenant.id, deptName, deptDescription); setDeptName(''); setDeptDescription(''); })}>Add department</button>
        </div>}
        {departmentId && (
          <DepartmentPanel key={departmentId} departmentId={departmentId} tenant={tenant} manage={manage} platform={platform} me={me} onChanged={loadTenantData} onError={onError}
            onDeleted={() => { setDepartmentId(''); loadTenantData(); }} />
        )}
      </div>

      {manage && (
      <div className="bg-gray-800 border border-gray-700 rounded p-4 space-y-3">
        <h3 className="font-semibold">Role assignments <span className="text-xs font-normal text-gray-400">role = what, scope = tenant-wide or one department</span></h3>
        <p className="text-xs text-gray-400">
          Customer sign-in is not enabled yet; these assignments are the authorization model the next authentication phase will use.
          Legacy users keep their current access (dispatcher/observer → tenant-wide dispatcher/read-only).
        </p>
        <table className="w-full text-sm text-left">
          <thead className="text-gray-400"><tr><th className="py-1">User</th><th className="py-1">Role</th><th className="py-1">Scope</th><th /></tr></thead>
          <tbody>
            {roles.map((r) => (
              <tr key={r.id} className="border-t border-gray-700">
                <td className="py-1.5">{r.username}</td>
                <td className="py-1.5">{r.role}</td>
                <td className="py-1.5">{r.department_name ? `Department: ${r.department_name}` : 'Entire customer'}</td>
                <td className="py-1.5 text-right">
                  <button className="text-red-400 hover:text-red-300 text-xs" onClick={() => act(() => revokeRole(tenant.id, r.id))}>Remove</button>
                </td>
              </tr>
            ))}
            {roles.length === 0 && <tr><td colSpan={4} className="py-2 text-gray-400">No explicit role assignments</td></tr>}
          </tbody>
        </table>
        <div className="flex flex-wrap gap-2">
          <select value={grant.user} onChange={(e) => setGrant({ ...grant, user: e.target.value })} className={input} aria-label="User">
            <option value="">User…</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.username} ({u.legacy_role})</option>)}
          </select>
          <select value={grant.role} onChange={(e) => setGrant({ ...grant, role: e.target.value })} className={input} aria-label="Role">
            {TENANT_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <select value={grant.department} onChange={(e) => setGrant({ ...grant, department: e.target.value })} className={input} aria-label="Scope">
            <option value="">Entire customer</option>
            {departments.map((d) => <option key={d.id} value={d.id}>Department: {d.name}</option>)}
          </select>
          <button className={`${btn} bg-blue-600 hover:bg-blue-500`} disabled={!grant.user}
            onClick={() => act(() => grantRole(tenant.id, grant.user, grant.role, grant.department))}>Grant</button>
        </div>
        {users.length === 0 && <p className="text-xs text-gray-400">This customer has no users yet.</p>}
      </div>
      )}
    </section>
  );
}

function DepartmentPanel({ departmentId, tenant, onChanged, onDeleted, onError, manage, platform }: {
  manage: boolean;
  platform: boolean;
  me: Me | null;
  departmentId: string;
  tenant: Tenant;
  onChanged: () => void;
  onDeleted: () => void;
  onError: (msg: string | null) => void;
}) {
  const [detail, setDetail] = useState<DepartmentDetail | null>(null);
  const [radios, setRadios] = useState<InventoryRadio[]>([]);
  const [groups, setGroups] = useState<ScopedResource[]>([]);
  const [edit, setEdit] = useState({ name: '', description: '' });
  const [addRadio, setAddRadio] = useState('');
  const [addGroup, setAddGroup] = useState('');

  const load = async () => {
    try {
      // Candidates for scoping are only needed by someone who may change the scopes: the platform
      // reads the rental inventory, a tenant administrator its own networks' radios.
      const [d, r, g] = await Promise.all([
        getDepartment(departmentId),
        !manage ? [] : platform ? getInventory({ tenant_id: tenant.id, state: 'ASSIGNED' }) : tenantRadios(tenant),
        manage ? getTenantGroups(tenant.id) : [],
      ]);
      setDetail(d);
      setEdit({ name: d.name, description: d.description });
      setRadios(r);
      setGroups(g);
    } catch (err) {
      onError(message(err));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId]);

  const act = async (fn: () => Promise<unknown>) => {
    onError(null);
    try {
      await fn();
      await load();
      onChanged();
    } catch (err) {
      onError(message(err));
    }
  };

  if (!detail) return <p className="text-sm text-gray-400">Loading department…</p>;
  const scopedRadioIds = new Set(detail.radios.map((r) => r.id));
  const scopedGroupIds = new Set(detail.groups.map((g) => g.id));

  return (
    <fieldset disabled={!manage} className="border border-gray-600 rounded p-3 space-y-3 bg-gray-900/40">
      <div className="flex flex-wrap gap-2">
        <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className={input} aria-label="Department name" />
        <input value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} placeholder="Description" className={`${input} flex-1`} aria-label="Department description" />
        <button className={`${btn} bg-gray-700 hover:bg-gray-600`} disabled={!edit.name.trim()} onClick={() => act(() => updateDepartment(detail.id, edit.name, edit.description))}>Save</button>
        <button className={`${btn} bg-red-700 hover:bg-red-600`} onClick={async () => {
          if (!window.confirm(`Delete department "${detail.name}"? Its scopes and department role assignments are removed; radios, groups and users are not.`)) return;
          try {
            await deleteDepartment(detail.id);
            onDeleted();
          } catch (err) {
            onError(message(err));
          }
        }}>Delete</button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <h4 className="text-sm font-semibold mb-1">Radios in scope ({detail.radios.length})</h4>
          <ul className="text-sm space-y-1 max-h-48 overflow-y-auto">
            {detail.radios.map((r) => (
              <li key={r.id} className="flex justify-between gap-2">
                <span>{r.label} <span className="text-xs text-gray-400">{r.network_name}</span></span>
                <button className="text-red-400 hover:text-red-300 text-xs" onClick={() => act(() => updateDepartmentScopes(detail.id, 'radios', [], [r.id]))}>Remove</button>
              </li>
            ))}
          </ul>
          <div className="flex gap-2 mt-2">
            <select value={addRadio} onChange={(e) => setAddRadio(e.target.value)} className={`${input} flex-1`} aria-label="Radio to add">
              <option value="">Add radio of this customer…</option>
              {radios.filter((r) => !scopedRadioIds.has(r.id)).map((r) => <option key={r.id} value={r.id}>{r.radio_id} – {r.network_name}</option>)}
            </select>
            <button className={`${btn} bg-blue-600 hover:bg-blue-500`} disabled={!addRadio}
              onClick={() => act(async () => { await updateDepartmentScopes(detail.id, 'radios', [addRadio], []); setAddRadio(''); })}>Add</button>
          </div>
        </div>
        <div>
          <h4 className="text-sm font-semibold mb-1">Groups in scope ({detail.groups.length})</h4>
          <ul className="text-sm space-y-1 max-h-48 overflow-y-auto">
            {detail.groups.map((g) => (
              <li key={g.id} className="flex justify-between gap-2">
                <span>{g.name} <span className="text-xs text-gray-400">{g.network_name}</span></span>
                <button className="text-red-400 hover:text-red-300 text-xs" onClick={() => act(() => updateDepartmentScopes(detail.id, 'groups', [], [g.id]))}>Remove</button>
              </li>
            ))}
          </ul>
          <div className="flex gap-2 mt-2">
            <select value={addGroup} onChange={(e) => setAddGroup(e.target.value)} className={`${input} flex-1`} aria-label="Group to add">
              <option value="">Add group of this customer…</option>
              {groups.filter((g) => !scopedGroupIds.has(g.id)).map((g) => <option key={g.id} value={g.id}>{g.name} – {g.network_name}</option>)}
            </select>
            <button className={`${btn} bg-blue-600 hover:bg-blue-500`} disabled={!addGroup}
              onClick={() => act(async () => { await updateDepartmentScopes(detail.id, 'groups', [addGroup], []); setAddGroup(''); })}>Add</button>
          </div>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold mb-1">Members ({detail.members.length})</h4>
        {detail.members.length === 0 ? <p className="text-xs text-gray-400">Nobody holds a role scoped to this department.</p> : (
          <ul className="text-sm">{detail.members.map((m) => <li key={m.assignment_id}>{m.username} – {m.role}</li>)}</ul>
        )}
      </div>
    </fieldset>
  );
}

// A tenant administrator's radios: every rented radio of the tenant's networks (GET /api/radios
// is authorized per network by the server).
async function tenantRadios(tenant: Tenant): Promise<InventoryRadio[]> {
  const lists = await Promise.all(tenant.networks.map(async (n) => {
    const response = await fetch('/api/radios?limit=1000', { headers: { 'X-Org-ID': n.id } });
    if (!response.ok) return [];
    const radios: { id: string; radio_id: string; name: string }[] = (await response.json()) || [];
    return radios.map((r) => ({ id: r.id, radio_id: r.radio_id, name: r.name, model: '', status: '', state: 'ASSIGNED' as const, network_id: n.id, network_name: n.name, department_ids: [] }));
  }));
  return lists.flat();
}
